import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { Auth } from './auth.service';

export interface UbigeoItem {
  id: number;
  nombre: string;
  codigoUbigeo?: string;
}

export interface UbigeoAdminDto {
  id: number;
  nombre: string;
  codigoUbigeo?: string;
  isActive?: boolean;
  paisId?: number;
  departamentoId?: number;
  provinciaId?: number;
  padreNombre?: string;
}

export interface DistritoPadres {
  id: number;
  nombre: string;
  codigoUbigeo?: string;
  provinciaId: number;
  departamentoId: number;
  paisId: number;
}

@Injectable({ providedIn: 'root' })
export class UbigeoService {
  private apiUrl = `${environment.apiUrl}/ubigeo`;

  constructor(private http: HttpClient, private auth: Auth) {}

  private getHeaders() {
    const token = this.auth.getToken();
    return {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    };
  }

  getPaises(): Observable<UbigeoItem[]> {
    return this.http.get<UbigeoItem[]>(`${this.apiUrl}/paises`, this.getHeaders());
  }

  getDepartamentos(paisId: number): Observable<UbigeoItem[]> {
    return this.http.get<UbigeoItem[]>(`${this.apiUrl}/departamentos?paisId=${paisId}`, this.getHeaders());
  }

  getProvincias(departamentoId: number): Observable<UbigeoItem[]> {
    return this.http.get<UbigeoItem[]>(`${this.apiUrl}/provincias?departamentoId=${departamentoId}`, this.getHeaders());
  }

  getDistritos(provinciaId: number): Observable<UbigeoItem[]> {
    return this.http.get<UbigeoItem[]>(`${this.apiUrl}/distritos?provinciaId=${provinciaId}`, this.getHeaders());
  }

  getDistritoConPadres(distritoId: number): Observable<DistritoPadres> {
    return this.http.get<DistritoPadres>(`${this.apiUrl}/distrito/${distritoId}`, this.getHeaders());
  }

  // --- CRUD Admin ---
  getAllPaises(): Observable<UbigeoAdminDto[]> {
    return this.http.get<UbigeoAdminDto[]>(`${this.apiUrl}/paises/all`, this.getHeaders());
  }
  createPais(body: { nombre: string }): Observable<UbigeoItem> {
    return this.http.post<UbigeoItem>(`${this.apiUrl}/paises`, body, this.getHeaders());
  }
  updatePais(id: number, body: { nombre: string }): Observable<UbigeoItem> {
    return this.http.put<UbigeoItem>(`${this.apiUrl}/paises/${id}`, body, this.getHeaders());
  }
  togglePais(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/paises/${id}`, this.getHeaders());
  }

  getAllDepartamentos(): Observable<UbigeoAdminDto[]> {
    return this.http.get<UbigeoAdminDto[]>(`${this.apiUrl}/departamentos/all`, this.getHeaders());
  }
  createDepartamento(body: { nombre: string; paisId: number }): Observable<UbigeoItem> {
    return this.http.post<UbigeoItem>(`${this.apiUrl}/departamentos`, body, this.getHeaders());
  }
  updateDepartamento(id: number, body: { nombre: string; paisId?: number }): Observable<UbigeoItem> {
    return this.http.put<UbigeoItem>(`${this.apiUrl}/departamentos/${id}`, body, this.getHeaders());
  }
  toggleDepartamento(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/departamentos/${id}`, this.getHeaders());
  }

  getAllProvincias(): Observable<UbigeoAdminDto[]> {
    return this.http.get<UbigeoAdminDto[]>(`${this.apiUrl}/provincias/all`, this.getHeaders());
  }
  createProvincia(body: { nombre: string; departamentoId: number }): Observable<UbigeoItem> {
    return this.http.post<UbigeoItem>(`${this.apiUrl}/provincias`, body, this.getHeaders());
  }
  updateProvincia(id: number, body: { nombre: string; departamentoId?: number }): Observable<UbigeoItem> {
    return this.http.put<UbigeoItem>(`${this.apiUrl}/provincias/${id}`, body, this.getHeaders());
  }
  toggleProvincia(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/provincias/${id}`, this.getHeaders());
  }

  getAllDistritos(): Observable<UbigeoAdminDto[]> {
    return this.http.get<UbigeoAdminDto[]>(`${this.apiUrl}/distritos/all`, this.getHeaders());
  }
  createDistrito(body: { nombre: string; codigoUbigeo?: string; provinciaId: number }): Observable<UbigeoItem> {
    return this.http.post<UbigeoItem>(`${this.apiUrl}/distritos`, body, this.getHeaders());
  }
  updateDistrito(id: number, body: { nombre: string; codigoUbigeo?: string; provinciaId?: number }): Observable<UbigeoItem> {
    return this.http.put<UbigeoItem>(`${this.apiUrl}/distritos/${id}`, body, this.getHeaders());
  }
  toggleDistrito(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/distritos/${id}`, this.getHeaders());
  }
}
